import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import emailSpellChecker from '@zootools/email-spell-checker'
import InfoCard from './InfoCard.component'
import { theme } from 'styles/theme'
import { Trans } from 'react-i18next'

const emailSuggestionContentStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  fontSize: theme.fontSizes.medium
}

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
`

const fadeOut = keyframes`
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-4px); }
`

const NoticeWrapper = styled.div`
    position: relative;
    width: 100%;
`
const NoticeBox = styled.div< { state?: string } >`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    width: 100%;

    ${({ state }) =>
      state === 'entering' && css`
        animation: ${fadeIn} 0.2s ease-out forwards;
    `}
    ${({ state }) =>
      state === 'exiting' && css`
        animation: ${fadeOut} 0.15s ease-in forwards;
    `}
`

const SuggestionButton = styled.button`
    background: none;
    border: none;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    padding: 0;
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSizes.medium};
    margin-left: ${({ theme }) => theme.spacing.xsmall};
    &:hover { opacity: 0.9; }
    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.secondaryLight};
        border-radius: ${({ theme }) => theme.spacing.xsmall};
    }
`

interface EmailSpellCheckNoticeProps {
  email: string
  onAccept: (corrected: string) => void
}

const EmailSpellCheckNotice: React.FC<EmailSpellCheckNoticeProps> = ({ onAccept, email = '' }) => {
  const [suggestion, setSuggestion] = useState<string>()
  const noticeRef = useRef<HTMLDivElement>(null)

  const onMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    logEvent('web_userSelectedEmailSuggestion', { email: suggestion })

    e.preventDefault()
    onAccept(suggestion!)
  }

  useEffect(() => {
    const check = emailSpellChecker.run({ email })

    setSuggestion(check?.full)
  }, [email])

  useEffect(() => {
    const notice = noticeRef.current
    // With Continue moved above the keyboard (onboarding-keyboard-cta), the suggestion can appear behind it, so scroll it into view
    if (suggestion == null || notice == null || !document.documentElement.hasAttribute('data-keyboard-open')) return
    const footer = [...document.querySelectorAll('footer')].find((f) => f.parentElement?.contains(notice))
    const hidden = notice.getBoundingClientRect().bottom - (footer?.getBoundingClientRect().top ?? window.innerHeight)
    if (hidden > 0) window.scrollBy({ top: hidden, behavior: 'smooth' })
  }, [suggestion])

  return (
    <NoticeWrapper ref={noticeRef} aria-live="polite">
      <TransitionGroup>
        {suggestion && (
          <CSSTransition timeout={200}>
            {(state) => (
              <NoticeBox state={state} role="status">
                <InfoCard color={theme.colors.yellow50} cardContentStyles={emailSuggestionContentStyles}>
                  <Trans
                    i18nKey="email.suggestion"
                    values={{ email: suggestion }}
                    components={{
                      strong: (<SuggestionButton onPointerDown={onMouseDown} />)
                    }}
                  />
                </InfoCard>
              </NoticeBox>
            )}
          </CSSTransition>
        )}
      </TransitionGroup>
    </NoticeWrapper>
  )
}

export default EmailSpellCheckNotice
